<template>
  <div class="lever-viz-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$F₁ · L₁ = F₂ · L₂$</div>
    </div>

    <div class="main-layout vertical">
      <!-- 仿真演示区 -->
      <div class="simulation-stage" ref="canvasContainer">
        <!-- 实时数据叠加层 -->
        <div class="torque-hud">
          <div class="hud-item left">
            <label>左侧力矩 M₁</label>
            <span class="val">{{ torqueLeft.toFixed(1) }}</span>
          </div>
          <div class="hud-center">
            <div class="balance-indicator" :class="{ 'is-balanced': isBalanced }">
              <i :class="isBalanced ? 'el-icon-check' : 'el-icon-warning-outline'"></i>
              {{ isBalanced ? '已平衡' : '失衡中' }}
            </div>
          </div>
          <div class="hud-item right">
            <label>右侧力矩 M₂</label>
            <span class="val">{{ torqueRight.toFixed(1) }}</span>
          </div>
        </div>
      </div>

      <!-- 参数控制区 -->
      <div class="compact-controls">
        <div class="control-card-grid split">
          <div class="control-column">
             <div class="column-header">左侧动力参数</div>
             <div class="control-item">
                <div class="label-row"><span>动力臂 (L₁)</span> <span class="val">{{ l1 }} cm</span></div>
                <el-slider v-model="l1" :min="1" :max="10" size="small"></el-slider>
             </div>
             <div class="control-item">
                <div class="label-row"><span>动力大小 (F₁)</span> <span class="val">{{ f1 }} N</span></div>
                <el-slider v-model="f1" :min="1" :max="50" size="small"></el-slider>
             </div>
          </div>
          
          <div class="control-column">
             <div class="column-header">右侧阻力参数</div>
             <div class="control-item">
                <div class="label-row"><span>阻力臂 (L₂)</span> <span class="val">{{ l2 }} cm</span></div>
                <el-slider v-model="l2" :min="1" :max="10" size="small"></el-slider>
             </div>
             <div class="control-item">
                <div class="label-row"><span>阻力大小 (F₂)</span> <span class="val">{{ f2 }} N</span></div>
                <el-slider v-model="f2" :min="1" :max="50" size="small"></el-slider>
             </div>
          </div>
        </div>

        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>杠杆原理：</strong>当动力与动力臂的乘积等于阻力与阻力臂的乘积时，杠杆保持静止。通过调节参数，观察杠杆倾斜方向与力矩大小的演变。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as THREE from 'three';

export default {
  name: 'LeverViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      f1: 20,
      l1: 6,
      f2: 30,
      l2: 4,
      tiltAngle: 0,
      running: true
    }
  },
  computed: {
    torqueLeft() { return this.f1 * this.l1; },
    torqueRight() { return this.f2 * this.l2; },
    isBalanced() { return Math.abs(this.torqueLeft - this.torqueRight) < 1.0; }
  },
  mounted() { this.initThree(); },
  beforeDestroy() {
    this.running = false;
    cancelAnimationFrame(this.frameId);
    if (this.renderer) {
      this.renderer.dispose();
      this.renderer.forceContextLoss();
    }
  },
  methods: {
    initThree() {
      const container = this.$refs.canvasContainer;
      const width = container.clientWidth;
      const height = container.clientHeight;

      this.scene = new THREE.Scene();
      this.scene.background = new THREE.Color(0xf8fafc);

      this.camera = new THREE.PerspectiveCamera(40, width / height, 1, 1000);
      this.camera.position.set(0, 35, 85);
      this.camera.lookAt(0, 5, 0);

      this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      this.renderer.setSize(width, height);
      this.renderer.setPixelRatio(window.devicePixelRatio);
      container.appendChild(this.renderer.domElement);

      // Support (Fulcrum)
      const fulcrumGeo = new THREE.CylinderGeometry(0, 6, 20, 4);
      const fulcrumMat = new THREE.MeshPhongMaterial({ color: 0x1e293b });
      const fulcrum = new THREE.Mesh(fulcrumGeo, fulcrumMat);
      fulcrum.position.y = -2;
      this.scene.add(fulcrum);

      // Lever Bar
      this.leverGroup = new THREE.Group();
      this.leverGroup.position.y = 8;
      this.scene.add(this.leverGroup);

      const barGeo = new THREE.BoxGeometry(110, 3, 3);
      const barMat = new THREE.MeshPhongMaterial({ color: 0x475569 });
      const bar = new THREE.Mesh(barGeo, barMat);
      this.leverGroup.add(bar);

      // Weights
      const w1Geo = new THREE.BoxGeometry(8, 8, 8);
      const w1Mat = new THREE.MeshPhongMaterial({ color: 0xef4444 });
      this.weight1 = new THREE.Mesh(w1Geo, w1Mat);
      this.leverGroup.add(this.weight1);

      const w2Geo = new THREE.BoxGeometry(8, 8, 8);
      const w2Mat = new THREE.MeshPhongMaterial({ color: 0x3b82f6 });
      this.weight2 = new THREE.Mesh(w2Geo, w2Mat);
      this.leverGroup.add(this.weight2);

      const light = new THREE.DirectionalLight(0xffffff, 1);
      light.position.set(20, 100, 50);
      this.scene.add(light);
      this.scene.add(new THREE.AmbientLight(0xffffff, 0.6));

      this.animate();
    },
    animate() {
      if (!this.running) return;
      this.frameId = requestAnimationFrame(this.animate);

      // Physics logic
      const targetTilt = (this.torqueRight - this.torqueLeft) * 0.004;
      this.tiltAngle += (targetTilt - this.tiltAngle) * 0.08;
      this.leverGroup.rotation.z = this.tiltAngle;
      
      const s1 = Math.pow(this.f1 / 25, 0.5);
      this.weight1.scale.set(s1, s1, s1);
      this.weight1.position.set(-this.l1 * 5, -5.5, 0);

      const s2 = Math.pow(this.f2 / 25, 0.5);
      this.weight2.scale.set(s2, s2, s2);
      this.weight2.position.set(this.l2 * 5, -5.5, 0);

      this.renderer.render(this.scene, this.camera);
    }
  }
}
</script>

<style scoped lang="scss">
.lever-viz-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }

.viz-header-compact {
  display: flex; justify-content: space-between; align-items: center; padding: 0 4px;
  .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; }
  .formula-chip { background: #f0f7ff; color: #2563eb; border: 1px solid #dbeafe; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; }
}

.simulation-stage {
  flex: 1; min-height: 240px; background: #ffffff; border-radius: 12px; border: 1.5px solid #e2e8f0; position: relative; overflow: hidden;
  .torque-hud {
    position: absolute; top: 12px; left: 0; width: 100%; display: flex; justify-content: space-around; align-items: center; z-index: 10;
    .hud-item {
      text-align: center; background: rgba(255,255,255,0.8); padding: 4px 12px; border-radius: 8px; border: 1px solid #e2e8f0;
      label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; }
      .val { font-size: 16px; color: #1e293b; font-weight: 800; font-family: monospace; }
      &.left { border-left: 3px solid #ef4444; }
      &.right { border-left: 3px solid #3b82f6; }
    }
    .balance-indicator {
      display: flex; align-items: center; gap: 6px; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 800; background: #fee2e2; color: #ef4444;
      &.is-balanced { background: #f0fdf4; color: #16a34a; }
    }
  }
}

.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card-grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 12px; background: #ffffff; padding: 10px 14px; border-radius: 12px; border: 1px solid #e2e8f0;
  .control-column {
    .column-header { font-size: 11px; font-weight: 800; color: #94a3b8; margin-bottom: 8px; border-bottom: 1px solid #f1f5f9; }
    .control-item {
      margin-bottom: 4px;
      .label-row { display: flex; justify-content: space-between; .label { font-size: 12px; color: #64748b; } .val { font-size: 12px; color: #2563eb; font-weight: 700; } }
    }
  }
}

</style>
